import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { makeRemoteAddLocationUser } from 'main/factories/usecases/add-location-user';
import { FIVE_SECONDS, showToast, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS } from 'lib/utils';
import { AddLocationUser } from 'domain/usecases/add-location-user';

/**
 * Custom hook for adding a location user
 * @param {UseMutationOptions} options - Optional configuration options
 * @returns {Object} Mutation object with various properties and methods
 */
export const useAddLocationUser = (options?: UseMutationOptions<any, Error, AddLocationUser.Params, unknown>) => {
  const queryClient = useQueryClient();
  const addLocationUserService = makeRemoteAddLocationUser();

  // Function to add the location user via API
  const addLocationUserFn = async (userData: AddLocationUser.Params) => {
    return addLocationUserService.addLocationUser({ ...userData, password: '' });
  };

  const { onSuccess: customOnSuccess, ...rest } = options || {};

  return useMutation({
    mutationFn: addLocationUserFn,

    onError: (error, variables, _context) => {
      showToast(error.message || 'Error adding user', TOAST_ERROR, TEN_SECONDS);
    },

    // When the mutation succeeds, invalidate related queries
    onSuccess: async (_data, variables, _context) => {
      // Invalidate location users queries
      await queryClient.invalidateQueries({
        queryKey: ['locationUsers', variables.locationId],
      });

      // Invalidate locationsUsers query (used by useHoneGetUsers)
      await queryClient.invalidateQueries({
        queryKey: ['locationsUsers'],
      });

      showToast('User added successfully', TOAST_SUCCESS, FIVE_SECONDS);
    },

    // Forward other options
    ...rest,
  });
};
